import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Play,
  CheckCircle,
  Loader,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  Clock,
  FileText,
  Wrench,
  Search,
  Filter,
  CheckSquare,
  XSquare,
  Check,
  RefreshCw,
  XCircle,
} from "lucide-react";
import api from "../api/client";
import ScoreRing from "./ScoreRing";

const severityConfig = {
  critical: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-900/20", border: "border-red-800/30", dotColor: "bg-red-500" },
  warning: { icon: AlertCircle, color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-800/30", dotColor: "bg-amber-500" },
  info: { icon: Info, color: "text-blue-400", bg: "bg-blue-900/20", border: "border-blue-800/30", dotColor: "bg-blue-500" },
};

const CHECK_LABELS = {
  T001: "Missing Title", T002: "Duplicate Title", T003: "Title Too Long", T004: "Title Too Short",
  T006: "Title = H1", T007: "Title Keyword Stuffing",
  M001: "Missing Meta Description", M002: "Duplicate Meta Description", M003: "Meta Too Long", M004: "Meta Too Short",
  H001: "Missing H1", H002: "Multiple H1", H003: "Duplicate H1", H005: "No H2 Subheadings", H006: "H1 Keyword Stuffing",
  I001: "Missing Alt Text", I002: "Generic Alt Text", I005: "Missing Image Dimensions", I006: "Broken Images",
  L001: "Broken Internal Links", L003: "Redirect Chains", L005: "Orphan Pages", L006: "No Outbound Links",
  L007: "Nofollow Internal Links", L009: "Too Many Links",
  C001: "Thin Content", C002: "Duplicate Content", C003: "Keyword Stuffing", C005: "Missing CTA",
  C006: "Missing Phone Number", C009: "Placeholder Text",
  S001: "Missing Schema", S003: "Missing FAQ Schema",
  X001: "Missing Canonical", X003: "Noindex in Sitemap", X007: "Mixed Content",
  X008: "Missing OG Tags", X009: "HTTP Errors", X010: "Slow Pages",
};

export default function CrawlDashboard({ selectedSite }) {
  const [dashboard, setDashboard] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeView, setActiveView] = useState("overview");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [crawlJobs, setCrawlJobs] = useState([]);
  const [pages, setPages] = useState([]);
  const pollRef = useRef(null);

  const loadData = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const [dashRes, issuesRes] = await Promise.all([
        api.get(`/websites/${selectedSite.id}/crawl/dashboard/`),
        api.get(`/websites/${selectedSite.id}/crawl/issues/`),
      ]);
      setDashboard(dashRes.data);
      setIssues(issuesRes.data || []);
    } catch {}
  }, [selectedSite]);

  const loadJobs = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const res = await api.get(`/websites/${selectedSite.id}/crawl/jobs/`);
      setCrawlJobs(res.data || []);
    } catch {}
  }, [selectedSite]);

  const loadPages = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const res = await api.get(`/websites/${selectedSite.id}/crawl/pages/`);
      setPages(res.data || []);
    } catch {}
  }, [selectedSite]);

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    Promise.all([loadData(), loadJobs()]).finally(() => setLoading(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedSite, loadData, loadJobs]);

  useEffect(() => {
    if (activeView === "pages" && pages.length === 0) loadPages();
  }, [activeView, pages.length, loadPages]);

  const triggerCrawl = async () => {
    if (!selectedSite || running) return;
    setRunning(true);
    try {
      await api.post(`/websites/${selectedSite.id}/crawl/trigger/`);
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.get(`/websites/${selectedSite.id}/crawl/jobs/`);
          const jobs = res.data || [];
          const latest = jobs[0];
          if (latest && (latest.status === "completed" || latest.status === "failed")) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setRunning(false);
            loadData();
            loadJobs();
          }
        } catch {}
      }, 5000);
    } catch { setRunning(false); }
  };

  const bulkFix = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      await api.post(`/websites/${selectedSite.id}/crawl/issues/bulk-fix/`, { ids });
      setSelectedIds(new Set());
      loadData();
    } catch {}
  };

  const bulkIgnore = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      await api.post(`/websites/${selectedSite.id}/crawl/issues/bulk-ignore/`, { ids });
      setSelectedIds(new Set());
      loadData();
    } catch {}
  };

  const bulkAction = async (action) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      await api.post(`/websites/${selectedSite.id}/crawl/issues/bulk-action/`, { ids, action });
      setSelectedIds(new Set());
      loadData();
    } catch {}
  };

  const issueAction = async (issueId, action) => {
    try {
      await api.post(`/websites/${selectedSite.id}/crawl/issues/${issueId}/${action}/`, {});
      loadData();
    } catch {}
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    const visible = filteredIssues.map(i => i.id);
    setSelectedIds(new Set(visible));
  };

  const filteredIssues = issues.filter(i => {
    if (severityFilter !== "all" && i.severity !== severityFilter) return false;
    if (categoryFilter !== "all" && i.category !== categoryFilter) return false;
    return true;
  });

  const categories = [...new Set(issues.map(i => i.category))].sort();

  if (!selectedSite) return <div className="text-gray-500 text-center py-20">Select a website</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Site Crawler</h2>
        <button
          onClick={triggerCrawl}
          disabled={running}
          className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          {running ? <Loader size={14} className="animate-spin" /> : <Play size={14} />}
          {running ? "Crawling..." : "Run Crawl"}
        </button>
      </div>

      {running && (
        <div className="bg-copper/10 border border-copper/30 rounded-xl p-4 flex items-center gap-3">
          <Loader size={18} className="animate-spin text-copper" />
          <span className="text-copper font-medium text-sm">Crawl in progress — auditing pages...</span>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-dark-300 rounded-xl" />
          <div className="h-64 bg-dark-300 rounded-xl" />
        </div>
      ) : !dashboard?.has_data ? (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center text-gray-500">
          No crawl data yet. Click "Run Crawl" to start.
        </div>
      ) : (
        <>
          {/* Score + Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 flex flex-col items-center justify-center">
              <ScoreRing score={dashboard.current.health_score} label="Health" size={90} />
              {dashboard.score_change != null && (
                <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${
                  dashboard.score_change > 0 ? "text-green-400" : dashboard.score_change < 0 ? "text-red-400" : "text-gray-500"
                }`}>
                  {dashboard.score_change > 0 ? <ArrowUp size={12} /> : dashboard.score_change < 0 ? <ArrowDown size={12} /> : null}
                  {dashboard.score_change > 0 ? "+" : ""}{dashboard.score_change} from last
                </div>
              )}
            </div>
            {[
              { label: "Pages", value: dashboard.current.pages_crawled, icon: FileText },
              { label: "Issues", value: dashboard.current.issues_found, icon: AlertTriangle },
              { label: "Errors", value: dashboard.current.critical_count, color: "text-red-400", icon: AlertTriangle },
              { label: "Warnings", value: dashboard.current.warning_count, color: "text-amber-400", icon: AlertCircle },
              { label: "Notices", value: dashboard.current.info_count, color: "text-blue-400", icon: Info },
            ].map(({ label, value, color, icon: Icon }) => (
              <div key={label} className="bg-dark-300 border border-dark-50 rounded-xl p-5">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><Icon size={12} />{label}</div>
                <div className={`text-2xl font-mono font-bold ${color || "text-white"}`}>{value}</div>
              </div>
            ))}
          </div>

          {/* Health Trend */}
          {dashboard.trend?.length > 1 && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                <TrendingUp size={14} /> Health Score Trend
              </h3>
              <div className="flex items-end gap-1 h-28">
                {dashboard.trend.map((point, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-500">{point.score}</span>
                    <div
                      className={`w-full rounded-t transition-all ${
                        point.score >= 90 ? "bg-green-500/60" : point.score >= 70 ? "bg-amber-500/60" : "bg-red-500/60"
                      }`}
                      style={{ height: `${Math.max(4, point.score)}%` }}
                    />
                    <span className="text-[10px] text-gray-600 truncate w-full text-center">
                      {point.recorded_at ? new Date(point.recorded_at).toLocaleDateString("en", { month: "short", day: "numeric" }) : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* View tabs */}
          <div className="flex gap-1 bg-dark-300 border border-dark-50 rounded-lg p-1">
            {[
              { id: "overview", label: "Top Issues" },
              { id: "issues", label: "All Issues" },
              { id: "pages", label: "Pages" },
              { id: "jobs", label: "Job History" },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveView(id)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeView === id ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* TOP ISSUES VIEW */}
          {activeView === "overview" && (
            <div className="space-y-2">
              {(dashboard.top_issues || []).map((item) => {
                const config = severityConfig[item.severity] || severityConfig.info;
                const Icon = config.icon;
                return (
                  <div key={item.check_id} className={`${config.bg} border ${config.border} rounded-lg flex items-center gap-3 p-4`}>
                    <div className={`w-2 h-2 rounded-full ${config.dotColor}`} />
                    <Icon size={16} className={config.color} />
                    <span className="text-sm font-medium text-gray-200 flex-1">
                      {CHECK_LABELS[item.check_id] || item.check_id}
                    </span>
                    <span className="text-xs text-gray-500 capitalize">{item.category}</span>
                    <span className="text-sm font-mono font-bold text-gray-300">{item.count}</span>
                    {item.is_auto_fixable && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-900/30 text-green-400 flex items-center gap-1">
                        <Wrench size={10} /> Fixable
                      </span>
                    )}
                  </div>
                );
              })}
              {!dashboard.top_issues?.length && (
                <div className="text-gray-500 text-center py-8">No issues found</div>
              )}
            </div>
          )}

          {/* ALL ISSUES VIEW */}
          {activeView === "issues" && (
            <div className="space-y-4">
              {/* Filters + bulk actions */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex gap-1 bg-dark-400 rounded-lg p-0.5">
                  {["open", "resolved", "all"].map(st => (
                    <button
                      key={st}
                      onClick={() => {
                        const params = st === "all" ? "?status=all" : st === "resolved" ? "?status=resolved" : "";
                        api.get(`/websites/${selectedSite.id}/crawl/issues/${params}`).then(r => {
                          // Trigger reload with new status filter
                          loadData();
                        });
                      }}
                      className="px-3 py-1 text-xs font-medium rounded-md transition-colors bg-dark-300 text-gray-400 hover:text-white"
                    >
                      {st.charAt(0).toUpperCase() + st.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  {["all", "critical", "warning", "info"].map(sev => (
                    <button
                      key={sev}
                      onClick={() => setSeverityFilter(sev)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        severityFilter === sev ? "bg-copper/20 text-copper" : "bg-dark-300 text-gray-400 hover:text-white"
                      }`}
                    >
                      {sev === "all" ? "All" : sev.charAt(0).toUpperCase() + sev.slice(1)}
                    </button>
                  ))}
                </div>
                <select
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs text-gray-300"
                >
                  <option value="all">All Categories</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex-1" />
                {selectedIds.size > 0 && (
                  <div className="flex gap-2">
                    <button onClick={bulkFix} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-900/30 text-green-400 rounded-lg hover:bg-green-900/50">
                      <Wrench size={12} /> Auto-Fix ({selectedIds.size})
                    </button>
                    <button onClick={() => bulkAction("fix")} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-900/20 text-green-300 rounded-lg hover:bg-green-900/40">
                      <Check size={12} /> Mark Fixed
                    </button>
                    <button onClick={() => bulkAction("dismiss")} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-800 text-gray-400 rounded-lg hover:bg-gray-700">
                      <XCircle size={12} /> Dismiss
                    </button>
                    <button onClick={() => bulkAction("recheck")} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-900/30 text-blue-400 rounded-lg hover:bg-blue-900/50">
                      <RefreshCw size={12} /> Re-check
                    </button>
                  </div>
                )}
                <button onClick={selectAllVisible} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-dark-300 text-gray-400 rounded-lg hover:text-white">
                  <CheckSquare size={12} /> Select All
                </button>
              </div>

              {/* Issues list */}
              <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
                <div className="max-h-[600px] overflow-y-auto">
                  {filteredIssues.slice(0, 200).map(issue => {
                    const config = severityConfig[issue.severity] || severityConfig.info;
                    const Icon = config.icon;
                    const isSelected = selectedIds.has(issue.id);
                    return (
                      <div key={issue.id} className={`flex items-start gap-3 px-4 py-3 border-b border-white/5 last:border-b-0 ${isSelected ? "bg-copper/5" : "hover:bg-dark-50/30"}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(issue.id)}
                          className="mt-1 rounded"
                        />
                        <Icon size={14} className={`${config.color} mt-0.5 flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-300">
                            [{issue.check_id}] {CHECK_LABELS[issue.check_id] || issue.description}
                          </div>
                          <div className="text-xs text-gray-500 truncate mt-0.5">{issue.page_url}</div>
                          {issue.current_value && (
                            <div className="text-xs text-gray-600 mt-0.5">Current: {issue.current_value}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            issue.fix_status === "fixed" || issue.fix_status === "marked_fixed" ? "bg-green-900/30 text-green-400" :
                            issue.fix_status === "queued" ? "bg-copper/20 text-copper" :
                            issue.fix_status === "ignored" || issue.fix_status === "dismissed" ? "bg-gray-800 text-gray-500" :
                            issue.fix_status === "auto_resolved" ? "bg-blue-900/30 text-blue-400" :
                            "bg-dark-400 text-gray-400"
                          }`}>{issue.fix_status === "marked_fixed" ? "fixed" : issue.fix_status === "auto_resolved" ? "resolved" : issue.fix_status}</span>
                          {issue.fix_status === "open" && (
                            <>
                              <button onClick={() => issueAction(issue.id, "mark-fixed")} className="p-1 rounded hover:bg-green-900/30 text-gray-600 hover:text-green-400 transition-colors" title="Mark as Fixed">
                                <Check size={12} />
                              </button>
                              <button onClick={() => issueAction(issue.id, "dismiss")} className="p-1 rounded hover:bg-gray-700/50 text-gray-600 hover:text-gray-300 transition-colors" title="Dismiss">
                                <XCircle size={12} />
                              </button>
                              <button onClick={() => issueAction(issue.id, "recheck")} className="p-1 rounded hover:bg-blue-900/30 text-gray-600 hover:text-blue-400 transition-colors" title="Re-check">
                                <RefreshCw size={12} />
                              </button>
                            </>
                          )}
                          {issue.is_auto_fixable && (
                            <Wrench size={12} className="text-green-500/50" title="Auto-fixable" />
                          )}
                          <a href={issue.page_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                  {!filteredIssues.length && (
                    <div className="text-gray-500 text-center py-8">No issues match filters</div>
                  )}
                </div>
                {filteredIssues.length > 200 && (
                  <div className="px-4 py-2 text-xs text-gray-500 border-t border-dark-50">
                    Showing 200 of {filteredIssues.length} issues
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PAGES VIEW */}
          {activeView === "pages" && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
              <div className="grid grid-cols-8 gap-2 px-4 py-3 text-xs text-gray-500 font-medium border-b border-dark-50">
                <span className="col-span-3">URL</span>
                <span>Status</span>
                <span>Words</span>
                <span>Speed</span>
                <span>Issues</span>
                <span>Schema</span>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {pages.slice(0, 200).map(page => (
                  <div key={page.id} className="grid grid-cols-8 gap-2 px-4 py-2.5 text-sm border-b border-dark-50/50 last:border-b-0 hover:bg-dark-50/30">
                    <span className="col-span-3 text-gray-300 truncate text-xs">{page.url.replace(/https?:\/\/[^/]+/, "")}</span>
                    <span className={`font-mono text-xs ${page.status_code === 200 ? "text-green-400" : page.status_code >= 400 ? "text-red-400" : "text-amber-400"}`}>
                      {page.status_code}
                    </span>
                    <span className="text-gray-400 text-xs">{page.word_count}</span>
                    <span className={`text-xs ${page.response_time_ms > 3000 ? "text-red-400" : page.response_time_ms > 1000 ? "text-amber-400" : "text-green-400"}`}>
                      {page.response_time_ms}ms
                    </span>
                    <span className="text-gray-400 text-xs">{page.issues_count}</span>
                    <span className="text-xs">{page.has_schema ? <CheckCircle size={12} className="text-green-400" /> : <span className="text-gray-600">--</span>}</span>
                  </div>
                ))}
                {!pages.length && (
                  <div className="text-gray-500 text-center py-8">No pages data</div>
                )}
              </div>
            </div>
          )}

          {/* JOB HISTORY VIEW */}
          {activeView === "jobs" && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
              <div className="grid grid-cols-8 gap-2 px-4 py-3 text-xs text-gray-500 font-medium border-b border-dark-50">
                <span className="col-span-2">Date</span>
                <span>Status</span>
                <span>Score</span>
                <span>Pages</span>
                <span className="text-red-400">Errors</span>
                <span className="text-amber-400">Warns</span>
                <span>Duration</span>
              </div>
              {crawlJobs.map(job => (
                <div key={job.id} className="grid grid-cols-8 gap-2 px-4 py-2.5 text-sm border-b border-dark-50/50 last:border-b-0 hover:bg-dark-50/30">
                  <span className="col-span-2 text-gray-400 text-xs">
                    {job.completed_at ? new Date(job.completed_at).toLocaleString() : job.created_at ? new Date(job.created_at).toLocaleString() : "--"}
                  </span>
                  <span className={`text-xs capitalize ${
                    job.status === "completed" ? "text-green-400" : job.status === "failed" ? "text-red-400" : "text-amber-400"
                  }`}>{job.status}</span>
                  <span className={`font-mono font-bold text-xs ${
                    job.health_score >= 90 ? "text-green-400" : job.health_score >= 70 ? "text-amber-400" : "text-red-400"
                  }`}>{job.health_score || "--"}</span>
                  <span className="text-gray-400 text-xs">{job.pages_crawled}</span>
                  <span className="text-red-400 font-mono text-xs">{job.critical_count}</span>
                  <span className="text-amber-400 font-mono text-xs">{job.warning_count}</span>
                  <span className="text-gray-500 text-xs">{job.duration_seconds ? `${job.duration_seconds}s` : "--"}</span>
                </div>
              ))}
              {!crawlJobs.length && (
                <div className="text-gray-500 text-center py-8">No crawl jobs yet</div>
              )}
            </div>
          )}

          {/* Last crawl info */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Clock size={12} />
              Last crawl: {dashboard.current.completed_at ? new Date(dashboard.current.completed_at).toLocaleString() : "Never"}
            </span>
            {dashboard.current.duration_seconds > 0 && (
              <span>Duration: {dashboard.current.duration_seconds}s</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
